import React from 'react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/modern/components/ui/dropdown-menu';
import {
  canArchive,
  canDelete,
  canShare,
  canCopyToBoard,
} from '../contextMenuUtils';
import { getEntityShareLink } from '@/other/utils/otherUtils';
import { showShareUrlOptions, showUnlinkRelatedEntityPrompt } from '@/other/utils/utils';
import { DEEP_LINK_URL_KEYS } from '@/other/constants/OtherKeys';
import { Resource } from '@/bryntum-lib/models/Resource';
import { toast } from 'sonner';
import { useConfirm } from '@omit/react-confirm-dialog';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { Model } from '@bryntum/schedulerpro';
import { Switch } from '@/modern/components/ui/switch';
import { cn } from '@/modern/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/modern/components/ui/tooltip';

interface ResourceContextMenuProps {
  record: Resource;
  onClose?: () => void;
  onItemClick?: (item: { name: string; }) => void;
}

const ResourceContextMenu: React.FC<ResourceContextMenuProps> = ({ record, onClose, onItemClick }) => {
  const context = useSwivuBryntumContext();
  const { user, project, selectedBoard, boardManager, viewManager } = context;
  const confirm = useConfirm();

  const isDemoUser = user.isDemoUser;

  // Permission checks
  const showArchive = canArchive(user, record);
  const showDelete = canDelete(user, record);
  const showShare = canShare(user, isDemoUser, record);
  const showCopyTo = canCopyToBoard(user, isDemoUser, selectedBoard, boardManager?.boardNameStore);

  // Action handlers
  const handleArchive = async () => {
    if (!record.archived && record.swivuUnlinkPromptConfig) {
      const isArchived = await showUnlinkRelatedEntityPrompt(record.swivuUnlinkPromptConfig);
      if (isArchived) {
        onClose?.();
      }
    } else {
      record.archived = !record.archived;
      onClose?.();
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete resource',
      description: `Are you sure you want to delete "${record?.name || 'this resource'}"? This action cannot be undone.`,
      confirmButton: {
        variant: 'destructive',
        title: 'Delete resource'
      },
      cancelButton: {
        variant: 'ghost'
      }
    });

    if (!confirmed) return;

    try {
      const recordStore = project?.resourceStore;
      if (recordStore) {
        recordStore.remove(record as unknown as Model);
        toast.success('Resource deleted successfully');
        onClose?.();

        if (viewManager) {
          viewManager.goToPreviousView();
        }
      }
    } catch (error) {
      console.error('Failed to delete resource:', error);
      toast.error('Something went wrong! Unable to delete resource');
    }
  };

  const handleShare = () => {
    const shareLink = getEntityShareLink({
      record,
      context,
      editorTabs: 'general',
      entityType: DEEP_LINK_URL_KEYS.resource,
      entityDetail: record,
      otherKeys: undefined,
    });
    showShareUrlOptions(shareLink);
    onClose?.();
  };

  const handleCopyTo = () => {
    onItemClick?.({ name: 'copyTo' });
  };

  const handleBlackoutToggle = (checked: boolean) => {
    record.hideAgenda = checked;
    toast.success(checked ? 'Resource will not be displayed on external schedules' : 'Resource will be displayed on external schedules');
  };

  return (
    <>
      {/* Share */}
      <DropdownMenuItem onClick={handleShare} disabled={!showShare}>
        <i className="fa-light fa-share mr-2" />
        <span>Share</span>
      </DropdownMenuItem>

      {/* Copy to */}
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleCopyTo} disabled={!showCopyTo}>
        <i className="fa-light fa-copy mr-2" />
        <span>Copy to</span>
      </DropdownMenuItem>

      {/* Separator before Blackout */}
      <DropdownMenuSeparator />

      {/* Blackout Toggle */}
      <div className="px-2 py-1.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <i
              className={cn(
                "flex-shrink-0 text-sm",
                record?.hideAgenda ? "text-destructive fa-regular fa-lightbulb-slash" : "text-primary fa-light fa-lightbulb-on"
              )}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  "text-sm font-medium",
                  record?.hideAgenda ? "text-destructive" : ""
                )}>
                  Blackout
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {record?.hideAgenda
                  ? "Don't display this resource on external schedules"
                  : "Display this resource on external schedules"}
              </TooltipContent>
            </Tooltip>
          </div>
          <Switch
            checked={record?.hideAgenda || false}
            onCheckedChange={handleBlackoutToggle}
            className={cn(
              "h-5 w-9",
              record?.hideAgenda && "data-[state=checked]:bg-destructive"
            )}
            thumbClassName="h-4 w-4 data-[state=checked]:translate-x-4"
          />
        </div>
      </div>

      {/* Separator before Archive/Delete */}
      <DropdownMenuSeparator />

      {/* Archive/Unarchive */}
      <DropdownMenuItem onClick={handleArchive} disabled={!showArchive}>
        <i className="fa-light fa-folder-open mr-2" />
        <span>{record?.archived ? 'Unarchive' : 'Archive'}</span>
      </DropdownMenuItem>

      {/* Delete */}
      <DropdownMenuItem onClick={handleDelete} className="text-destructive" disabled={!showDelete}>
        <i className="fa-light fa-trash mr-2" />
        <span>Delete</span>
      </DropdownMenuItem>
    </>
  );
};

export default ResourceContextMenu;